import { Platform } from 'react-native';
import data from './whats-new.json';

export type WhatsNewMatchPlatform = typeof Platform.OS;

export type WhatsNewPlatform = 'web' | 'ios' | 'android' | 'native' | 'all';
export type WhatsNewScreenSize = 'small' | 'large' | 'all';

export interface IWhatsNewItem {
  text: string;
  platform: WhatsNewPlatform;
  screenSize: WhatsNewScreenSize;
  dedupeKey?: string;
}

export interface IWhatsNewVersion {
  version: string;
  date: string;
  items: IWhatsNewItem[];
}

export interface IWhatsNewData {
  versions: IWhatsNewVersion[];
}

export const WHATS_NEW_LAST_SEEN_VERSION_STORAGE_KEY = 'whatsNewLastSeenVersion';
export const WHATS_NEW_AUTO_SHOW_DISABLED_STORAGE_KEY = 'whatsNewAutoShowDisabled';

export const whatsNew: IWhatsNewData = data as IWhatsNewData;

/**
 * Compares two semver-style version strings (e.g. "6.0.172" vs "6.0.165").
 * Returns negative if a<b, 0 if equal, positive if a>b.
 */
export function compareVersions(a: string, b: string): number {
  const aParts = a.split('.').map((part) => parseInt(part, 10) || 0);
  const bParts = b.split('.').map((part) => parseInt(part, 10) || 0);
  const length = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < length; i++) {
    const diff = (aParts[i] ?? 0) - (bParts[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export interface IWhatsNewMatchContext {
  platform: WhatsNewMatchPlatform;
  isLargeScreen: boolean;
}

/**
 * Returns true when the item should be shown for the given runtime context.
 * `platform` matches `web | ios | android | native | all` (`native` ⇒ ios/android).
 * `screenSize` matches `small | large | all`.
 */
export function matchesContext(
  item: IWhatsNewItem,
  context: IWhatsNewMatchContext,
): boolean {
  const platformOk =
    item.platform === 'all' ||
    item.platform === context.platform ||
    (item.platform === 'native' && (context.platform === 'ios' || context.platform === 'android'));
  const sizeOk =
    item.screenSize === 'all' ||
    (item.screenSize === 'large' && context.isLargeScreen) ||
    (item.screenSize === 'small' && !context.isLargeScreen);
  return platformOk && sizeOk;
}

/**
 * Newest release at or below `maxVersion` that has substantive matching items.
 * `versions` should be ordered newest-first (as in `whats-new.json`).
 */
export function findLatestVersionWithSubstantiveContent(
  maxVersion: string,
  versions: IWhatsNewVersion[],
  context: IWhatsNewMatchContext,
): IWhatsNewVersion | null {
  for (const v of versions) {
    if (compareVersions(v.version, maxVersion) > 0) continue;
    const hasSubstantive = v.items.some(
      (item) => matchesContext(item, context) && !item.dedupeKey,
    );
    if (hasSubstantive) return v;
  }
  return null;
}
